/**
 * Step Progress Indicator
 * Shows current step during execution + overall session totals
 */

import React from 'react';
import { Loader2, Activity } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useChatStore } from '@/stores/chat/store';

export const StepIndicator: React.FC = () => {
  const stepProgress = useChatStore((state) => state.stepProgress);
  const { activeSteps, isRunning, totalSteps, totalDuration, executionCount } = stepProgress;

  // Nothing to show if no steps have been tracked
  if (!isRunning && totalSteps === 0) {
    return null;
  }

  // Format duration
  const formatDuration = (ms: number): string => {
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    const mins = Math.floor(ms / 60000);
    const secs = ((ms % 60000) / 1000).toFixed(0);
    return `${mins}m ${secs}s`;
  };

  return (
    <div
      className={cn(
        'flex items-center justify-center gap-3 py-1.5 px-3 text-xs',
        'bg-muted/50 border-b border-border',
        'transition-all duration-300'
      )}
    >
      {/* Current step when running */}
      {isRunning && activeSteps.length > 0 && (
        <div className="flex items-center gap-1.5 text-primary">
          <Loader2 className="h-3 w-3 animate-spin" />
          <span>Step {activeSteps[activeSteps.length - 1]}...</span>
        </div>
      )}

      {/* Overall session stats */}
      {totalSteps > 0 && (
        <div className="flex items-center gap-1.5 text-muted-foreground">
          <Activity className="h-3 w-3" />
          <span>
            Total: {totalSteps} steps, {formatDuration(totalDuration)}
            {executionCount > 1 && ` (${executionCount} queries)`}
          </span>
        </div>
      )}
    </div>
  );
};
